import { useEffect, useRef } from "react";

/**
 * Ambient particle/dot wave on the hero. Subtle, blue dots, low opacity.
 * Static single frame under prefers-reduced-motion.
 */
export default function ParticleCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let raf = 0;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    // Multiple drifting layers
    const COLS = 90;
    const layers = [
      { amp: 18, freq: 0.018, speed: 0.0006, yBias: 0.62, opacity: 0.22, r: 1.3 },
      { amp: 28, freq: 0.012, speed: 0.0004, yBias: 0.75, opacity: 0.14, r: 1.6 },
      { amp: 40, freq: 0.008, speed: 0.0002, yBias: 0.9, opacity: 0.09, r: 2.0 },
    ];

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      layers.forEach((L) => {
        ctx.fillStyle = `rgba(37, 99, 235, ${L.opacity})`; // blue
        for (let i = 0; i < COLS; i++) {
          const x = (i / (COLS - 1)) * w;
          const y =
            h * L.yBias +
            Math.sin(i * L.freq * 50 + t * L.speed * 1000) * L.amp +
            Math.cos(i * L.freq * 25 + t * L.speed * 600) * (L.amp * 0.4);
          ctx.beginPath();
          ctx.arc(x, y, L.r, 0, Math.PI * 2);
          ctx.fill();
        }
      });
    };

    if (reduce) {
      draw(0);
    } else {
      const tick = (t) => {
        draw(t);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
      data-testid="hero-particle-canvas"
    />
  );
}
